/*
 * Long text field component
 */

import React from 'react';
import Base from './base.jsx';
import {
  Mode,
  castDataType,
  DataType,
} from '../common.jsx';
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";

import RichText from '../../common/RichText.jsx';

export default class RichTextField extends Base {

        static get CATEGORY() {
            return 'question';
        }

        static get TYPE() {
            return 'rich_text_field';
        }

        static icon(){
            return <i className="fa fa-align-left"></i>;
        }

        static get TRANSLATED_ATTRIBUTES() {
            return {
                label: 'field_default_label',
                placeholder: 'field_default_placeholder',
                description: 'field_default_description',
                help: null
            };
        }

        static defaultContent(language) {
            return Object.assign(super.defaultContent(language), {
                label: 'label',
                placeholder: 'placeholder',
                description: 'description',
                name: 'field_' + Helpers.randomString(),
                reportKey: '',
                help: 'help',
                required: false,
                label_hidden: false,
                charLimit: 0,
                lockEditWhenPrefilled: false,
                prettyView: {
                    hide: false,
                    show_label: false
                }
            });
        }

        constructor(props) {
            super(props);
            this.textareaId = Helpers.randomString(16); // Used in rich text editor as unique id
        }

        renderView() {
            return (<div>
                {this.getLockedInformationTooltip(this.modeFillOrView() && this.isReadOnlyPrefilledAnswer(), <>
                    <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
                        <span className="field-label">{this.getText('label')}</span>
                    {this.props.content.required ? <span className='required-field'> *</span> : null}
                    {this.helpIcon()}
                    {this.renderPrefillIcon()}
                    </label>
                    <RichText
                        id={this.textareaId}
                        fieldname={this.fullFieldName()}
                        charLimit={this.props.content.charLimit || 0}
                        readOnly={this.props.mode != Mode.FILL || this.isReadOnlyAnswer()}
                        value={this.getAnswer()}
                        placeholder={this.getText('placeholder')}
                        updateAnswer={(val) => this.answerChanged(val)}
                        preview={false}
                        className={this.getInputClassNames()}
                    />
                    {this.descriptionText()}
                    {!this.modeFillOrView() && this.isReadOnlyAnswer() ? this.lockedInformationDefault() : ''}
                </>)}
            </div>
            );
        }

        renderPrettyView() {
            const { hide = false, show_label = false } = this.props.content.prettyView || {};
            return (hide ? null :
                <>
                    {show_label &&
                        <label>
                            <span className="field-label">{this.getText('label')}</span>
                        </label>
                    }
                    <RichText 
                        id={this.textareaId}
                        fieldname={this.fullFieldName()}
                        readOnly={true}
                        value={this.getAnswer()}
                        placeholder={this.getText('placeholder')}
                        updateAnswer={(val) => this.answerChanged(val)}
                        preview={true}
                    />
                </>
            )
        }


        answerChanged = (value) => {
            this.updateAnswer(value);
            if (this.props.answersChangedCallback){
                this.props.answersChangedCallback();
            }
        }

        renderSettings() {
            return (
                <div>
                    <Setting.Checkbox name="required" label={I18n.t('field_required', {scope: 'fe'})} value={this.props.content.required} onChange={this.updateSettings} />
                    <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
                    <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />

                    {!this.props.settings['hidePlaceholderField'] &&
                      <Setting.TextField name="placeholder" label={I18n.t('field_placeholder', {scope: 'fe'})} value={this.getText('placeholder')} onChange={this.updateSettings} />
                    }
                    {!this.props.settings['hideDescriptionField'] &&
                      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
                    }
                    {!this.props.settings['hideHelpTextField'] &&
                      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
                    }
                    <Setting.TextField name="charLimit" label={I18n.t('field_char_limit', {scope: 'fe'})} value={this.props.content.charLimit} onChange={this.updateSettings} description={I18n.t('field_char_limit_description', {scope: 'fe'})} />
                    <Setting.TextField
                      name="reportKey"
                      label={I18n.t('field_report_key', {scope: 'fe'})}
                      value={this.props.content.reportKey}
                      description={I18n.t('field_report_key_description', {scope: 'fe'})}
                      {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                    />
                    <Setting.Checkbox name="lockEdit" label={I18n.t('field_lock_edit', {scope: 'fe'})} description={I18n.t('field_lock_edit_description', {scope: 'fe'})} value={this.props.content.lockEdit} onChange={this.updateSettings} />
                    {(!this.props.settings['hideLockEditWhenPrefilled'] && AppConfig.features.form_prefill) &&
                      <Setting.Checkbox name="lockEditWhenPrefilled" label={I18n.t('field_lock_edit_when_prefilled', {scope: 'fe'})} description={I18n.t('field_lock_edit_when_prefilled_description', {scope: 'fe'})} value={this.props.content.lockEditWhenPrefilled} onChange={this.updateSettings} />
                    }
                    {!this.props.settings['hideContainsSensitiveInformation'] &&
                      <Setting.Checkbox
                        name="containsSensitiveInformation"
                        label={I18n.t('field_contains_sensitive_information', {scope: 'fe'})}
                        description={I18n.t('field_contains_sensitive_information_description', {scope: 'fe'})}
                        value={this.props.content.containsSensitiveInformation}
                        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
                      />
                    }
                    {this.renderPrettyViewSettings()}
                </div>
            );
        }

        componentDidMount() {
            super.componentDidMount();
            this.componentDidUpdate();

            if (this.refs.textarea) {
                // Scale textarea height to content height but only max 400px.
                jQuery(this.refs.textarea).on('input propertychange', (e) => {
										jQuery(e.currentTarget).closest('div').attr('style', 'height: ' + e.currentTarget.scrollHeight +'px')
										jQuery(e.currentTarget).css('height', 'auto');
										let height = e.currentTarget.scrollHeight > 400 ? 400 : e.currentTarget.scrollHeight;
										jQuery(e.currentTarget).closest('div').removeAttr('style');
                    // Dealing with Textarea Height(if scrollHeight == 0) => min-height + lines x line-height + border
                    let newHeight = (height == 0) ? 34 + e.target.value.split(/\r?\n|\r/).length * 20 + 2 : height;
                    jQuery(e.currentTarget).outerHeight(newHeight + 10);
								}).trigger('input'); 
            }
        }

        componentDidUpdate() {
            super.componentDidUpdate();
            
            if (this.refs.textarea) {
                Helpers.TextareaCounter.remove(this.refs.textarea);

            }
        }

        beforeUpdateSettings(content) {
            content.required = castDataType(content.required, DataType.BOOLEAN);
            content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
            content.charLimit = castDataType(content.charLimit, DataType.INTEGER);
            content.lockEditWhenPrefilled = castDataType(content.lockEditWhenPrefilled, DataType.BOOLEAN);
            content.lockEdit = castDataType(content.lockEdit, DataType.BOOLEAN);
            this.beforeUpdateSettingsPrettyView(content);
        }

        getFormattedPrefillAnswer() {
            return "";
        }
}
